import { lock, unlock } from 'tua-body-scroll-lock';
import * as focusTrap from 'focus-trap';
import { createMediaQueryList, isAtLeast } from '../utils/_breakpoints';
import { getCurrentPageClass } from '../utils/_pageClass';
import { getFocusableElements, setTabindex } from '../utils/_domFunctions';

/**
 * Open or close a mobile level 2 accordion
 * @param {HTMLElement} anchorEl level 2 link controlling the accordion
 * @param {HTMLElement} listEl level 3 list
 * @param {boolean} [isOpen] forced state, toggles if undefined
 */
function toggleAccordion(anchorEl, listEl, isOpen) {
    const open = typeof isOpen === 'boolean' ? isOpen : anchorEl.getAttribute('aria-expanded') !== 'true';
    // Set max-height before aria-hidden: otherwise the CSS max-height is dropped (to none) before
    // the inline value is applied and the transition doesn't run on first opening
    // eslint-disable-next-line no-param-reassign
    listEl.style.maxHeight = open ? `${listEl.scrollHeight}px` : '0';
    anchorEl.setAttribute('aria-expanded', open);
    listEl.setAttribute('aria-hidden', !open);
}

class oNavigation extends HTMLElement {
    constructor() {
        super();
        this.level1 = this.querySelector('.level-1');
        this.toggleMobileMenuButton = this.querySelector('.toggle-menu');
        this.scrollPosition = 0;
        this.lastScrollTop = 0;
        this.levelLinks = this.querySelectorAll('.level-link');
        this.backButtons = this.querySelectorAll('.back-link');
        this.iconButtons = this.querySelectorAll('.icons button');
        this.currentPage = getCurrentPageClass();
        this.trigger = document.querySelector('#o-navigation-trigger');
        this.waitBeforeScrollEvent = false;
        this.navItems = this.getNavItems();
        this.desktopMql = createMediaQueryList('md');
        this.focusTrap = focusTrap.createFocusTrap(this);
        this.noScrollListener = 'noScrollEvent' in this.dataset;

        /**
         * List of elements that you don't want the touch/scroll gesture to
         * go through and scroll the body page behind (only for iOS).
         * Used by the `tua-body-scroll-lock` library.
         * Note: `overscroll-behavior: none` is not a working alternative (Tested on iOS 17.2).
         */
        this.bodyScrollElements = [
            this.querySelector('.catalog-and-secondary'),
            ...this.querySelectorAll('.level-list'),
        ];

        this.updateNavMenuState();

        // Track GlobalE Shipping switcher modal state
        this.globalEShippingSwitcherShown = false;
        this.querySelectorAll('.globale-selector').forEach((elem) => {
            elem.addEventListener('click', () => {
                this.globalEShippingSwitcherShown = true;
                this.updateMobileMenuLockingState();
            }, { passive: true });
        });
        document.addEventListener('GlobalE:shippingSwitcherClosed', () => {
            this.globalEShippingSwitcherShown = false;
            this.updateMobileMenuLockingState();
        });

        // Track login sidebar state
        this.loginSidebar = document.getElementById('o-sidebar-login');
        if (this.loginSidebar) {
            const config = { attributes: true, childList: false, subtree: false };
            this.sidebarObserver = new MutationObserver(() => {
                this.updateMobileMenuLockingState();
            });
            this.sidebarObserver.observe(this.loginSidebar, config);
        }

        // this binding
        this.toggleMobileMenu = this.toggleMobileMenu.bind(this);
        this.expandLevel1Menu = this.expandLevel1Menu.bind(this);
        this.collapseLevel1Menu = this.collapseLevel1Menu.bind(this);
        this.onLevel1LinkMouseEnter = this.onLevel1LinkMouseEnter.bind(this);
        this.onLevel1LinkMouseLeave = this.onLevel1LinkMouseLeave.bind(this);
        this.toggleLevel = this.toggleLevel.bind(this);
        this.goBack = this.goBack.bind(this);
        this.performantScrollListener = this.performantScrollListener.bind(this);
        this.loop = this.loop.bind(this);
        this.scrollEvents = this.scrollEvents.bind(this);
        this.initCurrentBar = this.initCurrentBar.bind(this);
        this.updateNavMenuState = this.updateNavMenuState.bind(this);
    }

    connectedCallback() {
        this.initCurrentBar();
        this.toggleMobileMenuButton.addEventListener('click', this.toggleMobileMenu);
        this.performantScrollListener();
        this.initMouseEvents();

        this.levelLinks.forEach((levelLink) => {
            levelLink.addEventListener('click', this.toggleLevel);
        });

        this.backButtons.forEach((backButton) => {
            backButton.addEventListener('click', this.goBack);
        });

        this.querySelectorAll('.nav-expand-btn').forEach((btn) => {
            btn.addEventListener('click', this.onNavExpandBtnClick.bind(this));
        });

        this.desktopMql.addEventListener('change', this.updateNavMenuState);

        document.addEventListener('oNavigation:updateCartCount', this.updateCartCount);

        this.setActiveItem();
    }

    disconnectedCallback() {
        this.desktopMql.removeEventListener('change', this.updateNavMenuState);
    }

    initCurrentBar() {
        switch (this.currentPage) {
            case 'pdp':
                this.currentBar = document.querySelector('[is="m-pdp-bar"]');
                this.currentPageHasBar = true;
                break;
            case 'plp':
            case 'search':
                this.currentBar = document.querySelector('[is="m-plp-bar"]');
                this.currentPageHasBar = this.currentBar !== null;
                break;
            default:
                break;
        }
    }

    updateCartCount(e) {
        if (e && e.detail && Object.hasOwnProperty.call(e.detail, 'productCount')) {
            // There's 2 mini-cart buttons:
            // one in nav and one in search sidebar (for mobile fake-nav)
            const cartContainers = this.querySelectorAll('.icons .mini-cart');

            cartContainers.forEach((cartContainer) => {
                if (e.detail.productCount === 0) {
                    cartContainer.querySelector('.cart-count').innerText = '';
                    cartContainer.classList.remove('active');
                    cartContainer.setAttribute('data-set-active', '');
                } else {
                    cartContainer.querySelector('.cart-count').innerText = e.detail.productCount;
                    cartContainer.classList.add('active');
                    cartContainer.removeAttribute('data-set-active');
                }
            });
        }
    }

    setActiveItem() {
        const cgid = this.getAttribute('data-current-page');
        if (!cgid) return;

        const itemActive = document.getElementById(cgid);
        if (!itemActive) return;

        const itemActiveParent = itemActive.closest('.level-1-item');
        if (!itemActiveParent) return;

        const levelLink = itemActiveParent.querySelector('.level-link');
        if (!levelLink) return;

        levelLink.classList.add('level-1-active');
    }

    /*
    This set the mouseenter and mouseleave event for desktop
    */
    initMouseEvents() {
        // After page load, if cursor is already on the nav, there will be no "mouserenter" event.
        // This listener will assure that the hover state will be correctly set in that case.
        this.addEventListener('pointerover', this.onNavigationPointerEnter, { once: true });

        this.addEventListener('pointerenter', this.onNavigationPointerEnter);
        this.addEventListener('pointerleave', this.onNavigationPointerLeave);

        const level1SubmenuLinks = this.querySelectorAll('.level-1-item:not(.has-no-submenu)');
        level1SubmenuLinks.forEach((level1SubmenuLink) => {
            level1SubmenuLink.addEventListener('mouseenter', (e) => this.expandLevel1Menu(e.currentTarget));
            level1SubmenuLink.addEventListener('mouseleave', () => this.collapseLevel1Menu());
        });

        const level1RedirectionLinks = this.querySelectorAll('.level-1-item.had-no-submenu');
        level1RedirectionLinks.forEach((level1lRedirectionLink) => {
            level1lRedirectionLink.addEventListener('mouseenter', this.onLevel1LinkMouseEnter);
            level1lRedirectionLink.addEventListener('mouseleave', this.onLevel1LinkMouseLeave);
        });
    }

    getNavListItem(item, parent = null) {
        const anchor = item.querySelector(':scope > .level-link, :scope > .level-1-inner > .level-link');
        const list = item.querySelector(':scope > ul');
        let childrens = [];
        if (list) {
            childrens = [...list.querySelectorAll(':scope > .level-item')]
                .map((el) => this.getNavListItem(el, item));
        }
        return {
            self: item,
            parent,
            anchor,
            list,
            childrens,
        };
    }

    getNavItems() {
        const root = document.getElementById('mainNavigation');
        const tree = [...root.querySelectorAll(':scope > .level-1-item')]
            .map((el) => this.getNavListItem(el));
        const list = [];
        const flat = (item) => {
            list.push(item);
            item.childrens.forEach((i) => flat(i));
        };
        tree.forEach((i) => flat(i));
        return { tree, list };
    }

    updateNavMenuState() {
        if (this.desktopMql.matches) {
            // Accordions are mobile only, reset them before removing their aria attributes
            this.closeAccordions();
            this.querySelectorAll('.has-accordion > .level-list').forEach((listEl) => {
                listEl.style.removeProperty('max-height');
            });
            this.navItems.list.forEach((item) => {
                if (item.anchor) {
                    item.anchor.removeAttribute('role');
                    item.anchor.removeAttribute('aria-controls');
                    item.anchor.removeAttribute('aria-expanded');
                    item.anchor.removeAttribute('tabindex');
                }
            });
        } else {
            this.navItems.list.forEach((item) => {
                if (item.childrens.length > 0) {
                    // Item contains sub-category
                    if (item.anchor) {
                        item.anchor.setAttribute('role', 'button');
                        item.anchor.setAttribute('aria-controls', item.list.id);
                        if (item.self.classList.contains('has-accordion')) {
                            item.anchor.setAttribute('aria-expanded', item.list.getAttribute('aria-hidden') === 'false');
                        }
                    }
                }
            });
        }
    }

    expandLevel1Menu(levelItem) {
        // Desktop only
        if (!this.desktopMql.matches) {
            return;
        }

        this.dataset.desktopMenuOpened = true;
        const targetedMenuList = levelItem.querySelector('.level-list');
        const targetedMenuTitle = levelItem.querySelector('.level-link');
        this.removeActiveMenuDesktopStyles();
        targetedMenuTitle.classList.add('level-1-hover');
        const currentHeaderTopPosition = this.getBoundingClientRect().top;
        const closeHeaderHeight = document.querySelector('#o-navigation-trigger').offsetHeight;
        document.documentElement.style.setProperty('--push-header-height', `${currentHeaderTopPosition + closeHeaderHeight}px`);
        document.querySelector('.page').classList.add('show-overlay');

        this.querySelectorAll('.level-list.level-2').forEach((levelList) => {
            levelList.setAttribute('aria-hidden', levelList === targetedMenuList ? 'false' : 'true');
        });

        const targetMenuHeight = targetedMenuList.offsetHeight;
        document.documentElement.style.setProperty('--current-desktop-sub-menu-height', `${targetMenuHeight}px`);
    }

    collapseLevel1Menu() {
        // Desktop only
        if (!this.desktopMql.matches) {
            return;
        }

        this.dataset.desktopMenuOpened = false;
        this.removeActiveMenuDesktopStyles();
        document.querySelector('.page').classList.remove('show-overlay');

        this.querySelectorAll('.level-list.level-2').forEach((levelList) => {
            levelList.setAttribute('aria-hidden', 'true');
        });

        document.documentElement.style.setProperty('--current-desktop-sub-menu-height', '0');
    }

    onNavExpandBtnClick(e) {
        const btn = e.currentTarget;

        const levelItem = btn.closest('.level-1-item');
        const levelList = levelItem.querySelector('.level-list');

        if (levelList.getAttribute('aria-hidden') === 'true') {
            this.expandLevel1Menu(levelItem);
        } else {
            this.collapseLevel1Menu();
        }
    }

    onNavigationPointerEnter(e) {
        if (e.pointerType !== 'mouse') {
            return;
        }
        this.dataset.mouseHovering = true;
    }

    onNavigationPointerLeave(e) {
        if (e.pointerType !== 'mouse') {
            return;
        }
        this.dataset.mouseHovering = false;

        if (this.dataset.scrollDirection === 'down') {
            document.documentElement.style.setProperty('--push-header-height', '0');
        }
    }

    onLevel1LinkMouseEnter(e) {
        // Desktop only
        if (!this.desktopMql.matches) {
            return;
        }

        const targetedMenuTitle = e.currentTarget.querySelector('.level-link');
        this.removeActiveMenuDesktopStyles();
        targetedMenuTitle.classList.add('level-1-hover');
    }

    onLevel1LinkMouseLeave() {
        // Desktop only
        if (!this.desktopMql.matches) {
            return;
        }

        this.removeActiveMenuDesktopStyles();
    }

    removeActiveMenuDesktopStyles() {
        const activedMenus = this.querySelectorAll('.level-1-hover');

        activedMenus.forEach((menuItem) => {
            menuItem.classList.remove('level-1-hover');
        });
    }

    /*
    This toggle the mobile menu :
    isShow = true : showing menu || isShow = false : hiding menu
    */
    toggleMobileMenu(e) {
        const isShow = this.dataset.currentLevel === '0';
        this.dataset.currentLevel = isShow ? '1' : '0';
        this.level1.setAttribute('aria-hidden', !isShow);
        e.currentTarget.setAttribute('aria-expanded', isShow);

        // hide all previous opened layers
        if (!isShow) {
            this.classList.remove('is-show');
            this.waitBeforeScrollEvent = true;
            this.updateMobileMenuLockingState();
            setTimeout(() => {
                this.waitBeforeScrollEvent = false;
                const openedLayers = this.querySelectorAll('[aria-hidden="false"]');
                openedLayers.forEach((openedLayer) => {
                    openedLayer.setAttribute('aria-hidden', true);
                });
                this.closeAccordions();
            }, 200);
            this.setTabindexes(this.level1, null);
        } else {
            this.classList.add('is-show');
            this.updateMobileMenuLockingState();
            this.setTabindexes(this.level1, null);
        }
    }

    /**
     * Activate or deactivate mobile menu focus trap according to current state of the component
     */
    updateMobileMenuLockingState() {
        // Check if current (mobile) menu is opened
        if (!this.classList.contains('is-show')) {
            this.focusTrap.deactivate();
            unlock(this.bodyScrollElements);
            return;
        }

        // Check if Login menu is opened
        if (this.loginSidebar.getAttribute('aria-expanded') === 'true') {
            this.focusTrap.deactivate();
            unlock(this.bodyScrollElements);
            return;
        }

        // GlobleE Shipping Switcher
        // Focus trap need to be deactivated so the user can interact with the modal
        if (this.globalEShippingSwitcherShown) {
            this.focusTrap.deactivate();
            unlock(this.bodyScrollElements);
            return;
        }

        lock(this.bodyScrollElements);
        this.focusTrap.activate();
    }

    /*
    This show the next level of mobile navigation
    */
    toggleLevel(e) {
        // Mobile only
        if (this.desktopMql.matches) {
            return;
        }

        const anchorEl = e.currentTarget;
        const listEl = document.getElementById(anchorEl.getAttribute('aria-controls'));
        if (!listEl) {
            return;
        }

        // Level 2 with subcategories opens as an accordion instead of a new level
        if (anchorEl.parentElement.classList.contains('has-accordion')) {
            e.preventDefault();
            toggleAccordion(anchorEl, listEl);
            return;
        }

        if (listEl.getAttribute('aria-hidden') !== 'true') {
            return;
        }

        e.preventDefault();
        const previousLevel = anchorEl.closest('.level-list');
        listEl.setAttribute('aria-hidden', 'false');
        this.setTabindexes(listEl, previousLevel);
        const nextLevelfirstFocusableElement = listEl.querySelector('.back-link, .level-link');
        this.dataset.currentLevel = parseInt(this.dataset.currentLevel, 10) + 1;
        // Small a11y improvment, setTimeout to prevent animation break:
        setTimeout(() => { nextLevelfirstFocusableElement.focus(); }, 200);
    }

    /*
    This closes every opened mobile accordion inside the given container (default: whole nav)
    */
    closeAccordions(container = this) {
        container.querySelectorAll('.has-accordion > .level-link[aria-expanded="true"]').forEach((anchorEl) => {
            const listEl = document.getElementById(anchorEl.getAttribute('aria-controls'));
            if (listEl) {
                toggleAccordion(anchorEl, listEl, false);
            }
        });
    }

    /*
    This set tabIndex to -1 or 0
    on mobile menu level toggling to keep the focus trapped elements focusable elements
    in current level only
    */
    setTabindexes(nextVisibleLayer, previousVisibleLayer) {
        // Mobile only
        if (this.desktopMql.matches) {
            return;
        }

        const focusableElements = [...getFocusableElements(nextVisibleLayer)]
            .filter((elm) => elm.closest('.level-list') === nextVisibleLayer); // filter elements that on other levels
        setTabindex(focusableElements, 0);

        if (previousVisibleLayer) {
            const notFocusableElements = [...getFocusableElements(previousVisibleLayer)]
                .filter((elm) => elm.closest('.level-list') === previousVisibleLayer); // filter elements that on other levels
            setTabindex(notFocusableElements, -1);
        }
    }

    /*
    This goes back to previous level
    on mobile back button click
    */
    goBack(e) {
        const currentButton = e.currentTarget;
        const currentLevel = currentButton.closest('.level-list');
        currentLevel.setAttribute('aria-hidden', true);
        this.dataset.currentLevel = parseInt(this.dataset.currentLevel, 10) - 1;

        const previousLevel = currentLevel.parentElement.closest('.level-list');
        this.setTabindexes(previousLevel, currentLevel);

        // Close accordions once the level slide transition is over
        setTimeout(() => { this.closeAccordions(currentLevel); }, 300);

        const previousAnchor = currentLevel.parentElement.querySelector(':scope > .level-link, :scope > .level-1-inner > .level-link');
        if (previousAnchor) {
            previousAnchor.focus();
        }
    }

    /*
    This inits an infinite loop using window.requestAnimationFrame
    for perfomant and bulletproof 'scroll' listener event
    see https://gist.github.com/Warry/4254579 for explanations
    */
    performantScrollListener() {
        this.lastScrollTop = window.scrollY;

        if (window.requestAnimationFrame) {
            this.loop();
        }
    }

    /*
    The infinite loop
    */
    loop() {
        // skip scroll if mobile and no scroll event (for cart)
        if (this.noScrollListener && !isAtLeast('md')) {
            window.requestAnimationFrame(this.loop);
            return;
        }
        const currentScrollTop = window.scrollY;
        // skip scroll events if we didn't scroll
        if (this.lastScrollTop === currentScrollTop) {
            window.requestAnimationFrame(this.loop);
            return;
        }
        // Skip scroll events and update this.lastScrollTop to prevent mobile shift navigation
        // after opening nav or sidebars on mobile.
        // If body is position fixed (triggered by body-scroll-lock) or until 200ms after removed
        // from position fixed (after sidebar close -> enable-body-scroll).
        if (document.body.style.position === 'fixed' || this.waitBeforeScrollEvent) {
            this.lastScrollTop = currentScrollTop;
            window.requestAnimationFrame(this.loop);
            return;
        }
        this.scrollEvents();
        this.lastScrollTop = currentScrollTop;

        window.requestAnimationFrame(this.loop);
    }

    /*
    set here the things you need to do like in a scroll listener callback function
    */
    scrollEvents() {
        // TODO: Opti: trigger this calculation only once when a change of above/below the trigger
        // has happened (like it was with the scroll observer).

        // This manage sticky / not sticky header changes with transitions
        const bannerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--header-banner-height'), 10);
        const triggerHeight = this.trigger.offsetHeight + bannerHeight;
        const { scrollY } = window;
        const transitionDelay = 200;

        // Manage scroll direction that will trigger the up & down with CSS
        if (scrollY > this.lastScrollTop) {
            this.setAttribute('data-scroll-direction', 'down');
            document.documentElement.style.setProperty('--push-header-height', '0px');
        } else {
            this.setAttribute('data-scroll-direction', 'up');
            document.documentElement.style.setProperty('--push-header-height', `${this.offsetHeight}px`);
        }

        // Filtering a PLP/Search page will regenerate the bar
        // so previous bar variable is broken > have to reint
        if (this.currentPageHasBar && !document.body.contains(this.currentBar)) {
            this.initCurrentBar();
        }

        if (scrollY > triggerHeight) {
            this.dataset.isSticky = true;
            if (this.currentPage === 'pdp') {
                this.currentBar.classList.add('transition');
            }
            setTimeout(() => {
                this.classList.add('add-transition'); // add class add-sticky to transition class to work well
                if (this.currentPageHasBar && (this.currentPage === 'plp' || this.currentPage === 'search')) {
                    this.currentBar.classList.add('add-transition');
                }
            }, transitionDelay);
        } else if (scrollY < this.trigger.offsetTop) { // the <div> is below the viewport
            this.dataset.isSticky = false;
            if (this.currentPage === 'pdp') { // remove class transition and add-sticky to avoid the display of m-pdp-bar on scroll top
                this.currentBar.classList.remove('transition');
            }
            setTimeout(() => {
                this.classList.remove('add-transition');
                if (this.currentPageHasBar && (this.currentPage === 'plp' || this.currentPage === 'search')) {
                    this.currentBar.classList.remove('add-transition');
                }
            }, transitionDelay);
        }
    }
}

customElements.define('o-navigation', oNavigation, {
    extends: 'header',
});
